import { FC, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import _fromPairs from "lodash/fromPairs";

import { clsxMerge } from "shared/lib/helpers";
import {
  BonusQualificationCriteriaTypeI,
  QualificationCriteriaTableTogglesValueI,
  QualificationTierI,
  MappedQCsAndPricingResponseI,
} from "shared/lib/interfaces/qualification-criteria";
import {
  getBonusCriteriaLabel,
  getCampaignTiers,
  getEarnedQualificationTier,
} from "shared/lib/helpers/qualification-criteria";
import { QualificationCriteriaTableWithToggles } from "shared/ui/qualification/table/with-toggles";
import { QC_TABLE_TOGGLE_STYLES } from "shared/ui/qualification/table/shared/qc-checkbox";
import { TriangleAlert } from "lucide-react";
import { Button } from "shared/ui/shadcn/button";

const PRESELECTED_QCS: QualificationCriteriaTableTogglesValueI = {
  companySizeCriteria: true,
  titleCriteria: true,
  industryCriteria: true,
  problemPainCriteria: true,
  timingBudgetCriteria: false,
  bonus2Criteria: false,
};

interface MarkMeetingFormPropsI {
  qcData: MappedQCsAndPricingResponseI;
  isQCLoading: boolean;
  isLoading: boolean;
  onSubmit: (
    meetingResult: "show" | "no_show",
    qualificationTier?: QualificationTierI
  ) => Promise<void>;
  onClose: () => void;
}

export const MarkMeetingForm: FC<MarkMeetingFormPropsI> = ({
  qcData,
  isQCLoading,
  isLoading,
  onSubmit,
  onClose,
}) => {
  const [meetingResult, setMeetingResult] = useState<"show" | "no_show" | null>(
    null
  );
  const [selectedQCs, setSelectedQCs] =
    useState<QualificationCriteriaTableTogglesValueI>(PRESELECTED_QCS);

  const tiers = useMemo(() => getCampaignTiers(qcData), [qcData]);

  const qualificationTier = useMemo(() => {
    if (meetingResult !== "show") {
      return undefined;
    }

    // The four base criteria are preselected and not editable here, so this
    // resolves to whichever bonus the rep picked, or base if none.
    return getEarnedQualificationTier(selectedQCs, tiers);
  }, [meetingResult, tiers, selectedQCs]);

  /* Selecting a bonus clears every other one. Passing null clears them all,
   * which is the "no bonus criterion met" option. */
  const handleSelectBonus = (
    selected: BonusQualificationCriteriaTypeI | null
  ) => {
    setSelectedQCs((prev) => ({
      ...prev,
      ..._fromPairs(tiers.bonusTypes.map((type) => [type, type === selected])),
    }));
  };

  const isSubmitDisabled = !meetingResult || isLoading;

  const handleSubmit = () => {
    if (!meetingResult) {
      return;
    }

    onSubmit(meetingResult, qualificationTier);
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <p className="typography-body-4-medium">Meeting result</p>

        <label
          className={clsxMerge(
            "flex cursor-pointer items-center gap-3 rounded-lg border p-4",
            "transition-colors",
            meetingResult === "show"
              ? "border-black bg-black/5"
              : "border-black/10 hover:border-black/30"
          )}
        >
          <input
            type="radio"
            name="meeting_result"
            value="show"
            checked={meetingResult === "show"}
            onChange={() => setMeetingResult("show")}
            className="radio radio-sm"
          />
          <div>
            <p className="typography-body-4-medium">Show</p>
            <p className="text-black/60 typography-body-5">
              The prospect attended the meeting
            </p>
          </div>
        </label>

        <label
          className={clsxMerge(
            "flex cursor-pointer items-center gap-3 rounded-lg border p-4",
            "transition-colors",
            meetingResult === "no_show"
              ? "border-black bg-black/5"
              : "border-black/10 hover:border-black/30"
          )}
        >
          <input
            type="radio"
            name="meeting_result"
            value="no_show"
            checked={meetingResult === "no_show"}
            onChange={() => setMeetingResult("no_show")}
            className="radio radio-sm"
          />
          <div>
            <p className="typography-body-4-medium">No-Show</p>
            <p className="text-black/60 typography-body-5">
              The prospect did not attend the meeting
            </p>
          </div>
        </label>
      </div>

      <AnimatePresence>
        {meetingResult === "show" && !isQCLoading && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-3">
              <p className="typography-body-4-medium">Qualification criteria</p>
              <p className="text-black/60 typography-body-5">
                Base criteria are pre-selected and cannot be changed.
                {tiers.tierCount === 2 &&
                  " Select the bonus criterion if it was also met."}
                {tiers.tierCount > 2 &&
                  " Select one bonus criterion if either was also met. Only one bonus can be earned."}
              </p>

              <QualificationCriteriaTableWithToggles
                texts={qcData}
                value={selectedQCs}
                onChange={setSelectedQCs}
                isEditable={false}
                tiers={tiers}
                toggleStyle={QC_TABLE_TOGGLE_STYLES.REGULAR}
              />

              {/* Bonuses are mutually exclusive, so these are radios with an
                  explicit "no bonus" option rather than checkboxes. */}
              {!!tiers.bonusTypes.length && (
                <div className="flex flex-col gap-2 rounded-lg bg-black/5 p-3">
                  {tiers.bonusTypes.map((type) => (
                    <label
                      key={type}
                      className="flex cursor-pointer items-center gap-2"
                    >
                      <input
                        type="radio"
                        name="bonus-criterion"
                        checked={!!selectedQCs[type]}
                        onChange={() => handleSelectBonus(type)}
                        className="radio radio-sm"
                      />
                      <span className="typography-body-4-medium">
                        {getBonusCriteriaLabel(type, tiers)} met
                        {qcData[type] ? `: ${qcData[type]}` : ""}
                      </span>
                    </label>
                  ))}

                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="bonus-criterion"
                      checked={!tiers.bonusTypes.some((t) => selectedQCs[t])}
                      onChange={() => handleSelectBonus(null)}
                      className="radio radio-sm"
                    />
                    <span className="typography-body-4-medium">
                      No bonus criterion met
                    </span>
                  </label>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {meetingResult === "show" && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="flex items-start gap-3 rounded-lg border border-[rgb(251,95,95)] bg-[rgb(251,95,95)]/5 p-4">
              <TriangleAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-[rgb(251,95,95)]" />
              <p className="text-[rgb(251,95,95)] typography-body-4">
                Incorrectly marking a meeting as Show will result in a permanent
                ban from the Glencoco platform.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex justify-end gap-3">
        <Button variant="outline" onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>

        <Button
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          loading={isLoading}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};
